import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import Loading from 'components/commons/loading/Loading';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import {
  getPortfolioAnalysis,
  getTargetDetail,
  updateTarget,
} from 'config/api';
import { useRouting } from 'hooks/useRouting';
import {
  TargetId,
  Target,
  ManagementType,
  ManagementItem,
  PortfolioAnalysisItem,
  TargetType,
  GlobalTargetProps,
  TargetModalStatus,
  PortfolioAssetType,
  PortfolioAnalysis,
} from '../utils/type';
import {
  getManagementItemInfo,
  portfolioTypeToBackgroundColor,
  portfolioTypeToString,
} from '../utils/portfolioInfo';
import { useBirthday } from '../utils/birthday';
import { PortfolioAnalysisLoading } from '../components/features/portfolio-analysis/share/PortfolioAnalysisLoading';
import { PortfolioChart } from '../components/features/portfolio-analysis/portfolio-chart';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';
import { dateToString } from '../utils/utils';

const PortfolioAnalysisChangeManagementConfirmInternal = ({
  targetId,
  selectedManagementType,
  target,
  portfolioAnalysis,
}: {
  targetId: TargetId;
  selectedManagementType: ManagementType;
  target: Target;
  portfolioAnalysis: PortfolioAnalysis;
}) => {
  const routing = useRouting();
  const goAssetSelect = (): void => routing.backRouting(true);

  const goAssetComplete = (): void =>
    routing.replaceRouting(
      {
        path: `/target/${targetId}/portfolio-analysis/change-assets/complete/${selectedManagementType}`,
      },
      true
    );

  const managementType: ManagementType = portfolioAnalysis.managementType;
  const managementItems: ManagementItem[] = portfolioAnalysis.managementItems;

  const initialManagementItemInfo = getManagementItemInfo(managementType);
  const selectedManagementItemInfo = getManagementItemInfo(
    selectedManagementType
  );
  const initialPortfolio = managementItems.find(
    managementItems => managementType === managementItems.managementType
  );
  const selectedPortfolio = managementItems.find(
    managementItems => selectedManagementType === managementItems.managementType
  );

  if (
    initialManagementItemInfo === undefined ||
    selectedManagementItemInfo === undefined ||
    initialPortfolio === undefined ||
    selectedPortfolio === undefined
  )
    return <Loading loading />;

  const dispInitialPortfolios = initialManagementItemInfo.dispType
    .map(dispType => {
      if (dispType === 'OTHER') return undefined;
      return initialPortfolio.itemsByPortfolioAnalysis.find(
        item => item.assetType === dispType
      );
    })
    .filter(e => e) as PortfolioAnalysisItem[] | undefined;

  const dispSelectedPortfolios = selectedManagementItemInfo.dispType
    .map(dispType => {
      if (dispType === 'OTHER') return undefined;
      return selectedPortfolio.itemsByPortfolioAnalysis.find(
        item => item.assetType === dispType
      );
    })
    .filter(e => e) as PortfolioAnalysisItem[] | undefined;

  if (
    dispInitialPortfolios === undefined ||
    dispSelectedPortfolios === undefined
  )
    return <></>;

  const update = async () => {
    const accountIDList = target.accountList.map(list => list.account);
    const mergeTarget = {
      ...target,
      accountIDList: accountIDList,
      managementType: selectedManagementType,
      startDate: new Date(target.startDate),
      endDate: new Date(target.endDate),
    };

    await updateTargetAPI(targetId, mergeTarget);
    goAssetComplete();
  };

  return (
    <div className="p-4 bg-white">
      <div className="pt-4 px-8 text-sm font-bold text-center">
        <span className={`${selectedManagementItemInfo.titleColor}`}>
          {selectedManagementItemInfo.title}
        </span>
        {selectedManagementItemInfo.subTitle}
        に
        <br />
        <span className="text-lg">変更してよろしいでしょうか？</span>
      </div>

      <div className="flex flex-row justify-evenly mx-2 mt-2">
        <div className="flex flex-col justify-center">
          <div className="mx-auto">
            <PortfolioChart
              {...{
                chart: {
                  title: '現在',
                  fontSize: '14px',
                },
                data: dispInitialPortfolios.map(p => {
                  return {
                    id: p.assetType,
                    value: p.targetPer,
                  };
                }),
                idToColor: portfolioTypeToBackgroundColor,
                idToTitle: portfolioTypeToString,
                valueFormatter: value =>
                  commaFormat(value, { numberOfDecimals: 0 }),
              }}
            />
          </div>
          <div className="flex flex-col items-center">
            <img
              style={{ width: '40px', height: '40px' }}
              src={`/static/images/advisor/asset_icon/${initialManagementItemInfo.imgName}.svg`}
              alt="アイコン"
            />
            <div className="mt-2 text-xs font-bold text-center leading-4">
              <span className={`${initialManagementItemInfo.titleColor}`}>
                {initialManagementItemInfo.title}
              </span>
              <br />
              {initialManagementItemInfo.subTitle}
            </div>
          </div>
          <div className="flex-col flex items-center mt-2">
            <div
              className="flex flex-col gap-2 leading-1"
              style={{ minHeight: '155px' }}>
              {dispInitialPortfolios.map((p, key) => {
                return (
                  <div className="flex flex-row items-center" key={key}>
                    <div
                      className="rounded-full w-3 h-3"
                      style={{
                        backgroundColor: portfolioTypeToBackgroundColor(
                          p.assetType
                        ),
                      }}></div>
                    <div className="ml-2 mr-6 text-xs whitespace-no-wrap">
                      {portfolioTypeToString(p.assetType)}
                    </div>
                    <div className="ml-auto text-xs">
                      {commaFormat(p.targetPer, {
                        numberOfDecimals: 1,
                      })}
                      %
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex flex-row relative top-12 icon-arrow_right text-gray-400 text-4xl"></div>
        <div className="flex flex-col justify-center">
          <div className="mx-auto">
            <PortfolioChart
              {...{
                chart: {
                  title: '変更',
                  fontSize: '14px',
                },
                data: dispSelectedPortfolios.map(p => {
                  return {
                    id: p.assetType,
                    value: p.targetPer,
                  };
                }),
                idToColor: portfolioTypeToBackgroundColor,
                idToTitle: portfolioTypeToString,
                valueFormatter: value =>
                  commaFormat(value, { numberOfDecimals: 0 }),
              }}
            />
          </div>
          <div className="flex flex-col items-center">
            <img
              style={{ width: '40px', height: '40px' }}
              src={`/static/images/advisor/asset_icon/${selectedManagementItemInfo.imgName}.svg`}
              alt="アイコン"
            />
            <div className="mt-2 text-xs font-bold text-center leading-4">
              <span className={`${selectedManagementItemInfo.titleColor}`}>
                {selectedManagementItemInfo.title}
              </span>
              <br />
              {selectedManagementItemInfo.subTitle}
            </div>
          </div>
          <div className="flex-col flex items-center mt-2">
            <div
              className="flex flex-col gap-2 leading-1"
              style={{ minHeight: '155px' }}>
              {dispSelectedPortfolios.map((p, key) => {
                return (
                  <div className="flex flex-row items-center" key={key}>
                    <div
                      className="rounded-full w-3 h-3"
                      style={{
                        backgroundColor: portfolioTypeToBackgroundColor(
                          p.assetType
                        ),
                      }}></div>
                    <div className="ml-2 mr-6 text-xs whitespace-no-wrap">
                      {portfolioTypeToString(p.assetType)}
                    </div>
                    <div className="ml-auto text-xs">
                      {commaFormat(p.targetPer, {
                        numberOfDecimals: 1,
                      })}
                      %
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col pt-8 mb-6 mx-2">
        <button
          className="font-bold text-base shadow text-white bg-tradewell rounded-lg p-2"
          onClick={() => update()}>
          変更する
        </button>
        <button
          className="text-bluegray-500 font-bold text-sm p-2 mt-4"
          onClick={(): void => goAssetSelect()}>
          前の画面に戻る
        </button>
      </div>
    </div>
  );
};

const PortfolioAnalysisChangeManagementConfirm = (): React.ReactElement => {
  const { id, type } = useParams<{ id: string; type: string }>();
  const selectedManagementType = Number(type) as ManagementType;

  useCustomHeaderTitle('運用タイプ変更画面');

  const { birthday } = useBirthday();

  const { data: target, isFetching: targetDetailIsFetching } = useQuery(
    'portfolioAnalysisChangeManagementConfirmGlobalTargetDetail',
    () => getTargetDetailAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: portfolioAnalysis,
    isFetching: portfolioAnalysisIsFetching,
  } = useQuery(
    'portfolioAnalysisChangeManagementConfirmPortfolioAnalysis',
    () => getPortfolioAnalysisAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (
    target === null ||
    target === undefined ||
    portfolioAnalysis === null ||
    portfolioAnalysis === undefined ||
    targetDetailIsFetching ||
    portfolioAnalysisIsFetching
  )
    return <PortfolioAnalysisLoading />;

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <PortfolioAnalysisChangeManagementConfirmInternal
        {...{ targetId: id, selectedManagementType, target, portfolioAnalysis }}
      />
    </PremiumCheck>
  );
};

const getTargetDetailAPI = async (id: string) => {
  const res_ = await getTargetDetail(id);
  const res = {
    id: res_.ID,
    title: res_.Title,
    refType: res_.RefType as TargetType,
    startDate: res_.StartDate,
    endDate: res_.EndDate,
    targetAmount: res_.TargetAmount,
    accountList: res_.AccountList.map(list => {
      return {
        account: list.Account,
        brokerName: list.BrokerName,
        available: list.Available,
        totalAssetAmount: list.TotalAssetAmount,
      };
    }),
    excludeAmount: res_.ExcludeAmount,
    reserveAmount: res_.ReserveAmount,
    assetAmount: res_.AssetAmount,
    yield: res_.Yield,
    achieved: res_.Achieved,
    status: res_.Status as TargetModalStatus,
  };

  return res as Target;
};

const getPortfolioAnalysisAPI = async (id: TargetId) => {
  const res_ = await getPortfolioAnalysis(id);
  const res = {
    itemsByPortfolioAnalysis: res_.ItemsByPortfolioAnalysis.map(item => {
      return {
        assetType: item.AssetType as PortfolioAssetType,
        currentPer: item.CurrentPer,
        targetPer: item.TargetPer,
      };
    }),
    registeredAccountName: res_.RegisteredAccountName,
    targetReturnPer: res_.TargetReturnPer,
    targetReturnLimitPer: res_.TargetReturnLimitPer,
    updateDateOfAsset: res_.UpdateDateOfAsset,
    managementType: res_.ManagementType as ManagementType,
    managementItems: res_.ManagementItems.map(managementItem => {
      return {
        managementType: managementItem.ManagementType as ManagementType,
        itemsByPortfolioAnalysis: managementItem.ItemsByPortfolioAnalysis.map(
          item => {
            return {
              assetType: item.AssetType as PortfolioAssetType,
              currentPer: item.CurrentPer,
              targetPer: item.TargetPer,
            };
          }
        ),
      };
    }),
  };

  return res as PortfolioAnalysis;
};

const updateTargetAPI = async (
  id: string,
  target: GlobalTargetProps
): Promise<{ tag: 'Success' | 'Error' }> => {
  const ret = await updateTarget({
    ID: id,
    TargetAmount: target.targetAmount,
    EndDate: dateToString(target.endDate),
    StartDate: dateToString(new Date()),
    Title: target.title,
    RefType: target.refType,
    ...(target.excludeAmount !== null &&
    target.reserveAmount !== null &&
    target.accountIDList !== null
      ? {
          AccountIDList: target.accountIDList,
          ExcludeAmount: target.excludeAmount,
          ReserveAmount: target.reserveAmount,
        }
      : {}),
    ...(target.managementType
      ? { ManagementType: target.managementType }
      : null),
  });

  if (ret.ResultCode === 0) {
    return { tag: 'Success' };
  } else {
    return { tag: 'Error' };
  }
};

export default PortfolioAnalysisChangeManagementConfirm;
